import { useEffect, useRef, useState } from 'react'
import { where } from 'firebase/firestore'
import Modal from '../components/Modal'
import Icon from '../components/Icon'
import FlightResultCard from '../components/FlightResultCard'
import { ErrorText, Field, Row, Text, TextArea, TimeZoneSelect } from '../components/Form'
import { ConfirmDelete } from './TravelDialogs'
import { useCollection } from '../lib/firestore'
import { useAirports, airportTimeZone, lookupAirport } from '../lib/airports'
import { saveFlight, deleteFlight, createCrewMemberByName } from '../lib/mutations'
import type { FlightDraft, PassengerDraft } from '../lib/mutations'
import { lookupFlight, normaliseFlightNumber } from '../lib/flightLookup'
import type { FlightLookupResult } from '../lib/flightLookup'
import { FlightLookupError } from '../lib/flightLookup'
import { browserTimeZone, toDateInputValue, toTimeInputValue } from '../lib/zonedTime'
import type { Flight, TourCrew, WithId } from '../types/models'

/**
 * Add or edit a flight.
 *
 * Starts from the one thing a crew always has — the flight number and the date
 * — and fills the rest in. The old flow made you pick a departure airport and
 * scroll a whole day of departures, which is the wrong shape for the job.
 *
 * When the lookup finds nothing (or isn't switched on) the number and date
 * carry straight into manual entry rather than being thrown away.
 */
export default function FlightDialog({
  open,
  flight,
  tourId,
  ownerId,
  onClose,
}: {
  open: boolean
  flight?: WithId<Flight> | null
  tourId: string
  ownerId: string
  onClose: () => void
}) {
  useAirports()
  const crew = useCollection<TourCrew>('tourCrew', [where('tourId', '==', tourId)])

  const [draft, setDraft] = useState<FlightDraft>(() => initialDraft(flight))
  const [picked, setPicked] = useState<FlightLookupResult | null>(null)
  const [showDetails, setShowDetails] = useState(!!flight)
  const [results, setResults] = useState<FlightLookupResult[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const patch = (next: Partial<FlightDraft>) => setDraft((previous) => ({ ...previous, ...next }))

  // An existing flight stores only `crewId` per passenger — the name is looked
  // up, not copied, so a crew rename doesn't leave a stale name on the flight.
  // That means the rows can't render until the crew query lands.
  useEffect(() => {
    if (crew.data.length === 0) return
    setDraft((previous) => {
      const needsNames = previous.passengers.some((passenger) => !passenger.name)
      if (!needsNames) return previous
      return {
        ...previous,
        passengers: previous.passengers.map((passenger) => ({
          ...passenger,
          name:
            passenger.name ||
            crew.data.find((member) => member.id === passenger.crewId)?.name ||
            'Removed from tour',
        })),
      }
    })
  }, [crew.data])

  const valid =
    draft.origin.trim().length > 0 &&
    draft.destination.trim().length > 0 &&
    draft.departureDate.length > 0 &&
    draft.arrivalDate.length > 0

  async function search() {
    setSearching(true)
    setSearchError(null)
    setResults([])
    try {
      const found = await lookupFlight(draft.flightNumber, draft.departureDate)
      setResults(found)
      // One result is the common case — take it rather than making someone
      // pick from a list of one. The card still shows so they can confirm it.
      if (found.length === 1) applyResult(found[0])
    } catch (caught) {
      setSearchError(
        caught instanceof FlightLookupError ? caught.message : (caught as Error).message,
      )
    } finally {
      setSearching(false)
      setHasSearched(true)
    }
  }

  function applyResult(result: FlightLookupResult) {
    const originZone = airportTimeZone(result.originIATA) ?? browserTimeZone()
    const destinationZone = airportTimeZone(result.destinationIATA) ?? browserTimeZone()
    patch({
      airline: result.airlineCode,
      flightNumber: result.flightNumber,
      origin: result.originIATA,
      destination: result.destinationIATA,
      originTimezone: originZone,
      destinationTimezone: destinationZone,
      departureDate: toDateInputValue(result.departureUTC, originZone),
      departureTime: toTimeInputValue(result.departureUTC, originZone),
      arrivalDate: toDateInputValue(result.arrivalUTC, destinationZone),
      arrivalTime: toTimeInputValue(result.arrivalUTC, destinationZone),
    })
    setPicked(result)
    // The fields stay folded away — the lookup filled them in, and opening a
    // form on top of a correct answer just invites second-guessing it.
    setShowDetails(false)
  }

  async function save() {
    if (!valid) return
    setBusy(true)
    setError(null)
    try {
      await saveFlight(draft, tourId, flight?.ownerId || ownerId, flight?.id)
      onClose()
    } catch (caught) {
      setError((caught as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      title={flight ? 'Edit flight' : 'Add flight'}
      width={620}
      onClose={onClose}
      footer={
        <>
          {flight && (
            <button
              className="btn btn-danger"
              style={{ marginRight: 'auto' }}
              disabled={busy}
              onClick={() => setConfirming(true)}
            >
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save flight'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ gap: 20 }}>
        {/* ------------------------------------------------------- lookup */}
        <section className="stack" style={{ gap: 10 }}>
          <h3 className="t-eyebrow">The flight</h3>
          <Row>
            <Field label="Flight number">
              <Text
                value={draft.flightNumber}
                onChange={(value) => patch({ flightNumber: value.toUpperCase() })}
                autoFocus={!flight}
              />
            </Field>
            <Field label="Date">
              <Text
                type="date"
                value={draft.departureDate}
                onChange={(value) => patch({ departureDate: value })}
              />
            </Field>
          </Row>

          {!picked && !flight && (
            <button
              className="btn btn-secondary"
              onClick={() => void search()}
              disabled={
                searching || !normaliseFlightNumber(draft.flightNumber) || !draft.departureDate
              }
            >
              {searching ? 'Searching…' : 'Find this flight'}
            </button>
          )}

          {searchError && !picked && (
            <div
              className="stack"
              style={{
                gap: 8,
                padding: '12px 14px',
                borderRadius: 'var(--radius-control)',
                background: 'color-mix(in srgb, var(--warning) 12%, transparent)',
              }}
            >
              <span className="t-caption" style={{ color: 'var(--warning)' }}>
                {searchError}
              </span>
              <button
                className="btn btn-secondary"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setShowDetails(true)}
              >
                Enter it manually
              </button>
            </div>
          )}

          {/* Several operations of the same number on one date — each drawn in
              full, because the difference between them is the times. */}
          {!picked && results.length > 1 && (
            <div className="stack" style={{ gap: 10 }}>
              <span className="t-caption">
                {results.length} operations of {draft.flightNumber} that day — pick one.
              </span>
              {results.map((result, index) => (
                <FlightResultCard
                  key={index}
                  result={result}
                  onSelect={() => applyResult(result)}
                />
              ))}
            </div>
          )}

          {/* The found flight, confirmed. */}
          {picked && (
            <div className="stack" style={{ gap: 8 }}>
              <FlightResultCard result={picked} selected />
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="btn btn-quiet"
                  style={{ paddingLeft: 0 }}
                  onClick={() => setShowDetails((current) => !current)}
                >
                  {showDetails ? 'Hide details' : 'Adjust route & times'}
                </button>
                <button
                  className="btn btn-quiet"
                  onClick={() => {
                    setPicked(null)
                    setResults([])
                    setHasSearched(false)
                    setSearchError(null)
                    setShowDetails(false)
                  }}
                >
                  Search again
                </button>
              </div>
            </div>
          )}

          {!picked && !hasSearched && !showDetails && !flight && (
            <button
              className="btn btn-quiet"
              style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
              onClick={() => setShowDetails(true)}
            >
              Skip the search and enter it manually
            </button>
          )}
        </section>

        {/* ------------------------------------------------------- manual */}
        {showDetails && (
          <section className="stack" style={{ gap: 10 }}>
            <h3 className="t-eyebrow">Route &amp; times</h3>

            <Row>
              <AirportField
                label="From"
                value={draft.origin}
                onChange={(iata) =>
                  patch({
                    origin: iata,
                    originTimezone: airportTimeZone(iata) ?? draft.originTimezone,
                  })
                }
              />
              <AirportField
                label="To"
                value={draft.destination}
                onChange={(iata) =>
                  patch({
                    destination: iata,
                    destinationTimezone: airportTimeZone(iata) ?? draft.destinationTimezone,
                  })
                }
              />
            </Row>

            <Row columns={3}>
              <Field label="Departs">
                <Text
                  type="date"
                  value={draft.departureDate}
                  onChange={(value) => patch({ departureDate: value })}
                />
              </Field>
              <Field label="Time">
                <Text
                  type="time"
                  value={draft.departureTime}
                  onChange={(value) => patch({ departureTime: value })}
                />
              </Field>
              <Field label="Timezone">
                <TimeZoneSelect
                  value={draft.originTimezone}
                  onChange={(value) => patch({ originTimezone: value })}
                />
              </Field>
            </Row>

            <Row columns={3}>
              <Field label="Arrives">
                <Text
                  type="date"
                  value={draft.arrivalDate}
                  onChange={(value) => patch({ arrivalDate: value })}
                />
              </Field>
              <Field label="Time">
                <Text
                  type="time"
                  value={draft.arrivalTime}
                  onChange={(value) => patch({ arrivalTime: value })}
                />
              </Field>
              <Field label="Timezone">
                <TimeZoneSelect
                  value={draft.destinationTimezone}
                  onChange={(value) => patch({ destinationTimezone: value })}
                />
              </Field>
            </Row>

          </section>
        )}

        <Field label="Booking reference" hint="The PNR the airline gave you.">
          <Text
            value={draft.bookingReference ?? ''}
            onChange={(value) => patch({ bookingReference: value.toUpperCase() })}
          />
        </Field>

        {/* --------------------------------------------------- passengers */}
        <PassengerEditor
          crew={crew.data}
          tourId={tourId}
          inviterId={ownerId}
          passengers={draft.passengers}
          onChange={(passengers) => patch({ passengers })}
        />

        <Field label="Notes">
          <TextArea value={draft.notes ?? ''} onChange={(value) => patch({ notes: value })} />
        </Field>

        <ErrorText>{error}</ErrorText>

        {confirming && flight && (
          <ConfirmDelete
            label={`${flight.origin} → ${flight.destination}`}
            busy={busy}
            onCancel={() => setConfirming(false)}
            onConfirm={async () => {
              setBusy(true)
              try {
                await deleteFlight(flight.id)
                onClose()
              } catch (caught) {
                setError((caught as Error).message)
              } finally {
                setBusy(false)
              }
            }}
          />
        )}
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------- passengers */

/**
 * Who's on the flight, with baggage and seat.
 *
 * Names come from this tour's crew rather than free text — a passenger has to
 * resolve to a `crewId` or it can't be shown on that person's own record, and
 * "which flight am I on" is the question the crew page exists to answer.
 */
function PassengerEditor({
  crew,
  tourId,
  inviterId,
  passengers,
  onChange,
}: {
  crew: WithId<TourCrew>[]
  tourId: string
  inviterId: string
  passengers: PassengerDraft[]
  onChange: (next: PassengerDraft[]) => void
}) {
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [adding, setAdding] = useState(false)
  const [justCreated, setJustCreated] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  const taken = new Set(passengers.map((passenger) => passenger.crewId))
  const matches = crew
    .filter((member) => !taken.has(member.id))
    .filter((member) =>
      query.trim() ? member.name.toLowerCase().includes(query.trim().toLowerCase()) : true,
    )
    .slice(0, 6)

  useEffect(() => setHighlight(0), [query])

  const totalBaggage = passengers.reduce(
    (total, passenger) => total + (Number(passenger.baggage || '0') || 0),
    0,
  )

  function add(member: WithId<TourCrew>) {
    onChange([...passengers, { crewId: member.id, name: member.name, baggage: '', seat: '' }])
    setQuery('')
    inputRef.current?.focus()
  }

  /**
   * Adds a name that isn't on the tour yet.
   *
   * Creates the crew record first so the passenger has a real `crewId` to hang
   * off — without one the flight can't appear on that person's crew card, which
   * is the whole point of the link.
   */
  async function addNewPerson(name: string) {
    const trimmed = name.trim()
    if (!trimmed || adding) return
    setAdding(true)
    try {
      const crewId = await createCrewMemberByName(trimmed, tourId, inviterId)
      onChange([...passengers, { crewId, name: trimmed, baggage: '', seat: '' }])
      setJustCreated((current) => [...current, trimmed])
      setQuery('')
      inputRef.current?.focus()
    } finally {
      setAdding(false)
    }
  }

  function update(crewId: string, next: Partial<PassengerDraft>) {
    onChange(
      passengers.map((passenger) =>
        passenger.crewId === crewId ? { ...passenger, ...next } : passenger,
      ),
    )
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <div className="spread">
        <h3 className="t-eyebrow">Passengers</h3>
        {passengers.length > 0 && (
          <span className="t-spec-caption dim">
            {passengers.length} · {totalBaggage} kg total
          </span>
        )}
      </div>

      {passengers.length > 0 && (
        <div className="panel rows">
          {passengers.map((passenger) => (
            <div
              key={passenger.crewId}
              className="row"
              style={{ padding: '10px 12px', gap: 8 }}
            >
              <span className="grow truncate" style={{ fontWeight: 600, minWidth: 0 }}>
                {passenger.name}
              </span>
              <label className="stack" style={{ gap: 3, flex: 'none' }}>
                <span className="t-eyebrow">Seat</span>
              <input
                className="field"
                style={{ width: 86 }}
                aria-label={`Seat for ${passenger.name}`}
                value={passenger.seat ?? ''}
                onChange={(event) =>
                  update(passenger.crewId, { seat: event.target.value.toUpperCase() })
                }
              />
              </label>
              <label className="stack" style={{ gap: 3, flex: 'none' }}>
                <span className="t-eyebrow">Bag kg</span>
              <input
                className="field"
                style={{ width: 86 }}
                type="number"
                min={0}
                aria-label={`Baggage for ${passenger.name}`}
                value={passenger.baggage ?? ''}
                onChange={(event) => update(passenger.crewId, { baggage: event.target.value })}
              />
              </label>
              <button
                aria-label={`Remove ${passenger.name}`}
                title="Remove"
                onClick={() =>
                  onChange(passengers.filter((entry) => entry.crewId !== passenger.crewId))
                }
                style={{ color: 'var(--text-tertiary)', padding: 4 }}
              >
                <Icon name="close" size={15} />
              </button>
            </div>
          ))}
        </div>
      )}

      {justCreated.length > 0 && (
        <div
          className="stack"
          style={{
            gap: 4,
            padding: '11px 14px',
            borderRadius: 'var(--radius-control)',
            background: 'var(--accent-wash)',
          }}
        >
          <span className="t-body" style={{ color: 'var(--accent-text)', fontWeight: 600 }}>
            {justCreated.join(', ')} added to the crew
          </span>
          <span className="t-caption">
            They're on the tour as a viewer but haven't been invited. Send them an invite from
            the Crew tab so they can actually see the schedule.
          </span>
        </div>
      )}

      <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            className="field"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setHighlight((current) => Math.min(current + 1, matches.length - 1))
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setHighlight((current) => Math.max(current - 1, 0))
              }
              if (event.key === 'Enter') {
                event.preventDefault()
                if (matches[highlight]) add(matches[highlight])
                else if (query.trim()) void addNewPerson(query)
              }
            }}
          />

          {query.trim().length > 0 && matches.length > 0 && (
            <div
              className="panel"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                zIndex: 5,
                overflow: 'hidden',
                boxShadow: 'var(--shadow-pop)',
              }}
            >
              {matches.map((member, index) => (
                <button
                  key={member.id}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => add(member)}
                  className="row"
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    gap: 10,
                    textAlign: 'left',
                    background: index === highlight ? 'var(--panel-raised)' : 'transparent',
                  }}
                >
                  <span className="grow truncate">{member.name}</span>
                  <span className="t-caption faint truncate">
                    {member.roles?.[0] ?? ''}
                  </span>
                </button>
              ))}
            </div>
          )}

          {query.trim().length > 0 && matches.length === 0 && (
            <button
              className="panel row"
              style={{ width: '100%', padding: '11px 14px', gap: 10, marginTop: 6, textAlign: 'left' }}
              disabled={adding}
              onClick={() => void addNewPerson(query)}
            >
              <Icon name="plus" size={16} style={{ color: 'var(--accent-text)' }} />
              <span className="grow">
                Add <strong>{query.trim()}</strong> to this tour
              </span>
              <span className="t-spec-caption faint">
                {adding ? 'Adding…' : 'Not on the crew yet'}
              </span>
            </button>
          )}
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------- airport */

function AirportField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (iata: string) => void
}) {
  const airport = lookupAirport(value)
  return (
    <Field label={label} hint={airport ? `${airport.city}, ${airport.countryCode}` : 'IATA code'}>
      <Text
        value={value}
        onChange={(next) => onChange(next.toUpperCase().slice(0, 3))}
      />
    </Field>
  )
}

/* ------------------------------------------------------------------ draft */

function initialDraft(flight?: WithId<Flight> | null): FlightDraft {
  const zone = browserTimeZone()
  if (!flight) {
    const today = toDateInputValue(new Date())
    return {
      airline: '',
      flightNumber: '',
      origin: '',
      destination: '',
      departureDate: today,
      departureTime: '',
      arrivalDate: today,
      arrivalTime: '',
      originTimezone: zone,
      destinationTimezone: zone,
      notes: '',
      bookingReference: '',
      passengers: [],
    }
  }

  const originZone = airportTimeZone(flight.origin) ?? zone
  const destinationZone = airportTimeZone(flight.destination) ?? zone
  const departure = flight.departureTimeUTC.toDate()
  const arrival = flight.arrivalTimeUTC.toDate()

  return {
    airline: flight.airline ?? '',
    flightNumber: flight.flightNumber ?? '',
    origin: flight.origin,
    destination: flight.destination,
    departureDate: toDateInputValue(departure, originZone),
    departureTime: toTimeInputValue(departure, originZone),
    arrivalDate: toDateInputValue(arrival, destinationZone),
    arrivalTime: toTimeInputValue(arrival, destinationZone),
    originTimezone: originZone,
    destinationTimezone: destinationZone,
    notes: flight.notes ?? '',
    bookingReference: flight.bookingReference ?? '',
    passengers: (flight.passengers ?? []).map((passenger) => ({
      crewId: passenger.crewId,
      name: '',
      baggage: passenger.baggage ?? '',
      seat: passenger.seat ?? '',
    })),
  }
}
